import React, { Component } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { DataStructure } from '../../domain/structure/DataStructureDomains';
import ValueFactory from '../../util/ValueFactory';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from 'constant/Injectables';
import CustomLoading from './CustomLoading';
import FormicaGroupedSearchSelectBox, { FormicaGroupSelectBoxItem, FormicaGroupSelectBoxProps } from './FormicaGroupedSearchSelectBox';
import DataType from '../../constant/DataType';
import { ArrayVariableProperties, CustomStructureProperties, VariableProperties } from '../../domain/datatype/VariablePropertiesDomains';
import SubscriptionContext = subscribe.SubscriptionContext;
import { FormicaSelectBoxItem } from './FormicaSearchSelectBox';
import FormicaGroupedSearchSelectBoxv2 from './FormicaGroupedSearchSelectBoxv2';
import LanguageUtil from '../../util/LanguageUtil';

class DataTypeSelectBoxComponent extends Component<Props, State> {
  public state = new State();

  private dataTypeSubscription: subscribe.Subscription | undefined;
  private dataStructureSubscriptions: subscribe.Subscription[] = [];
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  public render() {
    const { dataTypes, loading } = this.state;
    const { disabled, properties, ...rest } = this.props;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      dataTypes !== undefined && (
        <FormicaGroupedSearchSelectBox
          {...rest}
          options={dataTypes}
          placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dataType', defaultMessage: 'Data Type' })}
          value={this.getValue(properties)}
          rounded
          onChangeValue={this.props.onChangeValue}
        />
      )
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private getValue = (properties: VariableProperties | undefined): string | undefined => {
    if (properties == undefined) return;
    switch (properties.dataType) {
      case DataType.ARRAY:
        return `List(${this.getValue((properties as ArrayVariableProperties).properties)})`;
      case DataType.STRUCTURE:
        return (properties as CustomStructureProperties).name;
      default:
        return properties.dataType;
    }
  };

  private tracker = async () => {
    if (this.dataStructureSubscriptions.some((s) => s?.ready())) {
      await this.buildData();
    }
  };

  private startTracker = async () => {
    this.dataStructureSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.DATA_STRUCTURES);
    const dataTypeQuery = query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES);
    const isAllStopped = this.dataStructureSubscriptions.every((s) => !s.ready());
    if (this.dataStructureSubscriptions.length == 0 || isAllStopped) {
      this.dataTypeSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, dataTypeQuery.build());
      this.dataStructureSubscriptions = [this.dataTypeSubscription];
    }
    this.computation = subscribe.Tracker.autorun(this.tracker, this.dataStructureSubscriptions);
  };

  private stopTracker = async () => {
    await this.dataTypeSubscription?.stop();
    this.computation?.stop();
  };

  private buildData = async () => {
    const structEnabled = this.props.structEnabled ?? true;
    const dateEnabled = this.props.dateEnabled ?? true;
    const listEnabled = this.props.listEnabled ?? true;
    const dataTypeQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build();
    const dataTypes: DataStructure[] = await this.repository.findMany(dataTypeQuery);
    const defaultOptions: FormicaGroupSelectBoxItem = { groupName: 'Basic', items: ValueFactory.getDataTypeOptions() };
    if (!dateEnabled) {
      const dateIndex = defaultOptions.items.findIndex((d) => d.value == DataType.DATE);
      if (dateIndex != -1) defaultOptions.items.splice(dateIndex, 1);
    }
    const allOptions: FormicaGroupSelectBoxItem[] = [defaultOptions];
    const primitiveArrayItems: FormicaSelectBoxItem[] = ValueFactory.getDataTypeOptions().map((dataType: FormicaSelectBoxItem) => {
      return {
        label: dataType.label,
        value: `List(${dataType.value})`,
        properties: {
          dataType: DataType.ARRAY,
          properties: { dataType: dataType.value } as VariableProperties,
        } as ArrayVariableProperties,
      };
    });
    if (structEnabled) {
      const dataTypesWithGroupOptions: FormicaGroupSelectBoxItem = {
        groupName: 'Struct',
        items: dataTypes.map((dataType: DataStructure) => {
          return {
            label: dataType.name,
            value: dataType.name,
            properties: { dataType: DataType.STRUCTURE, name: dataType.name } as CustomStructureProperties,
          };
        }),
      };
      allOptions.push(dataTypesWithGroupOptions);
      if (listEnabled) {
        const dataTypesWithArrayItems: FormicaGroupSelectBoxItem = {
          groupName: 'List',
          items: dataTypes.map((dataType: DataStructure) => {
            return {
              label: dataType.name,
              value: `List(${dataType.name})`,
              properties: {
                dataType: DataType.ARRAY,
                properties: { dataType: DataType.STRUCTURE, name: dataType.name } as CustomStructureProperties,
              } as ArrayVariableProperties,
            };
          }),
        };
        allOptions.push(dataTypesWithArrayItems);
        dataTypesWithArrayItems.items.unshift(...primitiveArrayItems);
      }
    } else {
      if (listEnabled) {
        const primitiveListGroup: FormicaGroupSelectBoxItem = {
          groupName: 'List',
          items: primitiveArrayItems,
        };
        allOptions.push(primitiveListGroup);
      }
    }
    this.setState({ dataTypes: allOptions, loading: false });
  };
}

interface Props extends Omit<FormicaGroupSelectBoxProps, 'options'> {
  properties?: VariableProperties;
  structEnabled?: boolean;
  listEnabled?: boolean;
  dateEnabled?: boolean;
  disabledSelect?: boolean;
  'data-testid'?: string;
}

class State {
  dataTypes: FormicaGroupSelectBoxItem[] = [];
  loading: boolean = true;
}

export default DataTypeSelectBoxComponent;
